import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';

const {
  FiKey, FiEye, FiEyeOff, FiSave, FiCheck, FiAlertCircle, FiLock,
  FiLink, FiPercent, FiCalendar, FiCreditCard, FiZap,
  FiShield, FiInfo, FiDollarSign, FiClock, FiTrendingUp,
} = FiIcons;

// ── Permission guards ─────────────────────────────────────────────────────────
const AccessDeniedBanner = () => (
  <div className="flex flex-col items-center justify-center py-20 gap-6 bg-white rounded-[40px] border-2 border-dashed border-red-200">
    <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center border border-red-200">
      <SafeIcon icon={FiLock} className="w-10 h-10 text-red-400" />
    </div>
    <div className="text-center px-8">
      <h4 className="text-xl font-serif text-accent-navy mb-2">Access Restricted</h4>
      <p className="text-text-muted text-sm font-light max-w-md">
        Payment settings and configuration is only accessible to administrators with financial permissions.
      </p>
    </div>
  </div>
);

// ── Utility ───────────────────────────────────────────────────────────────────
const maskKey = (val) => {
  if (!val || val.length < 10) return val;
  return val.slice(0, 7) + '••••••••••••' + val.slice(-4);
};

// ── Gateway brand config ──────────────────────────────────────────────────────
const GATEWAY_META = {
  stripe:    { label: 'Stripe',    color: '#635BFF', emoji: '💳', bg: 'bg-[#635BFF]/10', text: 'text-[#635BFF]' },
  paystack:  { label: 'Paystack',  color: '#00C3F7', emoji: '⚡', bg: 'bg-[#00C3F7]/10', text: 'text-[#00C3F7]' },
  paypal:    { label: 'PayPal',    color: '#003087', emoji: '🅿',  bg: 'bg-[#003087]/10', text: 'text-[#003087]' },
  razorpay:  { label: 'Razorpay',  color: '#3395FF', emoji: '🔵', bg: 'bg-[#3395FF]/10', text: 'text-[#3395FF]' },
};

// ── Shared primitives ─────────────────────────────────────────────────────────
const Section = ({ icon, title, subtitle, children, accent = false }) => (
  <div className={`rounded-[32px] border p-8 space-y-6 ${accent ? 'bg-accent-navy border-accent-navy/20' : 'bg-white border-brand-dark'}`}>
    <div className={`flex items-center gap-4 pb-5 border-b ${accent ? 'border-white/10' : 'border-brand-dark'}`}>
      <div className={`p-3 rounded-2xl ${accent ? 'bg-white/10' : 'bg-brand-light'}`}>
        <SafeIcon icon={icon} className={`w-5 h-5 ${accent ? 'text-accent-gold' : 'text-accent-navy'}`} />
      </div>
      <div>
        <h3 className={`font-serif text-lg ${accent ? 'text-white' : 'text-accent-navy'}`}>{title}</h3>
        {subtitle && <p className={`text-[10px] font-black uppercase tracking-widest ${accent ? 'text-white/40' : 'text-text-muted'}`}>{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const KeyField = ({ label, hint, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          placeholder={hint}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full pl-4 pr-12 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs font-mono"
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy transition-colors"
        >
          <SafeIcon icon={visible ? FiEyeOff : FiEye} className="w-4 h-4" />
        </button>
      </div>
      {value && <p className="text-[9px] text-text-muted font-mono pl-1 opacity-60">{maskKey(value)}</p>}
      <p className="text-[10px] text-text-muted">{hint}</p>
    </div>
  );
};

const WebhookField = ({ label, value, onChange, hint }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">{label}</label>
      <div className="flex gap-2">
        <div className="relative flex-grow">
          <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input
            type="url"
            placeholder="https://your-server.com/webhooks/..."
            value={value}
            onChange={e => onChange(e.target.value)}
            className="w-full pl-10 pr-4 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs font-mono"
          />
        </div>
        <button
          type="button"
          onClick={copy}
          className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest border-2 transition-all flex items-center gap-1.5 ${copied ? 'bg-green-500 border-green-500 text-white' : 'bg-brand-light border-brand-dark text-text-muted hover:border-accent-gold'}`}
        >
          <SafeIcon icon={copied ? FiCheck : FiIcons.FiCopy} className="w-3.5 h-3.5" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {hint && <p className="text-[10px] text-text-muted">{hint}</p>}
    </div>
  );
};

const WebhookEvents = ({ events }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
    {events.map(ev => (
      <div key={ev} className="flex items-center gap-2 p-3 bg-brand-light rounded-xl border border-brand-dark">
        <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
        <span className="text-[9px] font-mono text-text-muted">{ev}</span>
      </div>
    ))}
  </div>
);

const InfoBox = ({ color, emoji, children }) => {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    sky:    'bg-sky-50 border-sky-200 text-sky-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className={`flex items-start gap-3 border rounded-2xl p-4 ${colors[color]}`}>
      {emoji ? <span className="text-lg flex-shrink-0">{emoji}</span> : <SafeIcon icon={FiInfo} className="w-4 h-4 mt-0.5 flex-shrink-0" />}
      <p className="text-[10px] leading-relaxed">{children}</p>
    </div>
  );
};

const ToggleRow = ({ label, desc, value, onChange }) => (
  <div className="flex items-center justify-between gap-4 p-5 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold/50 transition-colors group">
    <div>
      <p className="font-bold text-accent-navy text-sm group-hover:text-accent-gold transition-colors">{label}</p>
      <p className="text-[11px] text-text-muted leading-relaxed">{desc}</p>
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-12 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${value ? 'bg-accent-gold' : 'bg-brand-dark'}`}
    >
      <motion.div
        animate={{ x: value ? 24 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
      />
    </button>
  </div>
);

const GatewayStatusPill = ({ gateway, enabled, testMode }) => {
  const meta = GATEWAY_META[gateway];
  return (
    <div className={`p-5 rounded-[20px] border-2 flex items-center gap-3 ${enabled ? 'border-green-200 bg-green-50' : 'border-red-100 bg-red-50'}`}>
      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${enabled ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`} />
      <div className="min-w-0">
        <p className="text-xs font-black uppercase tracking-widest text-accent-navy">{meta.label}</p>
        <p className={`text-[10px] font-bold ${enabled ? 'text-green-600' : 'text-red-500'}`}>
          {enabled ? (testMode ? 'Test Mode' : 'Live') : 'Disabled'}
        </p>
      </div>
    </div>
  );
};

const SaveToast = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className="fixed bottom-8 right-8 z-[200] bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">Settings Saved</p>
          <p className="text-[10px] opacity-80">Payment configuration updated successfully.</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="p-6 rounded-[24px] border border-brand-dark bg-white flex items-start gap-4">
    <div className={`p-2.5 rounded-xl ${color}`}>
      <SafeIcon icon={icon} className="w-4 h-4 text-white" />
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{label}</p>
      <p className="text-2xl font-serif text-accent-navy mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-text-muted mt-0.5">{sub}</p>}
    </div>
  </div>
);

const GatewaySectionDivider = ({ gateway }) => {
  const meta = GATEWAY_META[gateway];
  return (
    <div className="flex items-center gap-3 pt-2 pb-1">
      <div className={`w-8 h-8 ${meta.bg} rounded-xl flex items-center justify-center text-base flex-shrink-0`}>
        {meta.emoji}
      </div>
      <span className={`text-xs font-black uppercase tracking-widest ${meta.text}`}>{meta.label}</span>
      <div className="flex-grow border-t border-brand-dark" />
    </div>
  );
};

// ── Constants ─────────────────────────────────────────────────────────────────
const PAYOUT_SCHEDULES = [
  { id: 'daily',    label: 'Daily',      desc: 'Every business day',    icon: '⚡' },
  { id: 'weekly',   label: 'Weekly',     desc: 'Every Monday',          icon: '📅' },
  { id: 'biweekly', label: 'Bi-Weekly',  desc: 'Every 14 days',         icon: '🗓️' },
  { id: 'monthly',  label: 'Monthly',    desc: '1st of each month',     icon: '📆' },
];

const STORAGE_KEY = 'em_payment_settings';
const loadSettings = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const PaymentSettings = () => {
  const { permissions } = useAuth();
  
  // ✅ Check if user has admin permissions to view/edit payment settings
  const isAdmin = permissions?.isGlobalAdmin?.() ?? false;
  // Alternative permission check for specific payment management permission
  const hasPaymentPermission = isAdmin || permissions?.canManagePayments?.();
  
  // Early return if no permission
  if (!hasPaymentPermission) {
    return <AccessDeniedBanner />;
  }

  const saved = loadSettings();

  // Stripe
  const [stripePublishable, setStripePublishable] = useState(saved.stripePublishable || '');
  const [stripeSecret,      setStripeSecret]      = useState(saved.stripeSecret      || '');
  const [stripeWebhook,     setStripeWebhook]     = useState(saved.stripeWebhook     || '');
  const [stripeEnabled,     setStripeEnabled]     = useState(saved.stripeEnabled     ?? true);

  // Paystack
  const [paystackPublic,  setPaystackPublic]  = useState(saved.paystackPublic  || '');
  const [paystackSecret,  setPaystackSecret]  = useState(saved.paystackSecret  || '');
  const [paystackWebhook, setPaystackWebhook] = useState(saved.paystackWebhook || '');
  const [paystackEnabled, setPaystackEnabled] = useState(saved.paystackEnabled ?? true);

  // PayPal
  const [paypalClientId,     setPaypalClientId]     = useState(saved.paypalClientId     || '');
  const [paypalClientSecret, setPaypalClientSecret] = useState(saved.paypalClientSecret || '');
  const [paypalWebhookId,    setPaypalWebhookId]    = useState(saved.paypalWebhookId    || '');
  const [paypalWebhook,      setPaypalWebhook]      = useState(saved.paypalWebhook      || '');
  const [paypalEnabled,      setPaypalEnabled]      = useState(saved.paypalEnabled      ?? false);

  // Razorpay
  const [razorpayKeyId,     setRazorpayKeyId]     = useState(saved.razorpayKeyId     || '');
  const [razorpayKeySecret, setRazorpayKeySecret] = useState(saved.razorpayKeySecret || '');
  const [razorpayWebhook,   setRazorpayWebhook]   = useState(saved.razorpayWebhook   || '');
  const [razorpayWebhookSecret, setRazorpayWebhookSecret] = useState(saved.razorpayWebhookSecret || '');
  const [razorpayEnabled,   setRazorpayEnabled]   = useState(saved.razorpayEnabled   ?? false);

  // Commission
  const [platformCommission, setPlatformCommission] = useState(saved.platformCommission ?? 10);
  const [merchantCommission, setMerchantCommission] = useState(saved.merchantCommission ?? 5);
  const [donationFee,        setDonationFee]        = useState(saved.donationFee        ?? 2.5);

  // Payout
  const [payoutSchedule,      setPayoutSchedule]      = useState(saved.payoutSchedule      || 'weekly');
  const [minPayoutAmount,     setMinPayoutAmount]     = useState(saved.minPayoutAmount     ?? 50);
  const [autoPayouts,         setAutoPayouts]         = useState(saved.autoPayouts         ?? true);
  const [payoutNotifications, setPayoutNotifications] = useState(saved.payoutNotifications ?? true);

  // Global toggles
  const [testMode,        setTestMode]        = useState(saved.testMode        ?? true);
  const [fraudProtection, setFraudProtection] = useState(saved.fraudProtection ?? true);

  // UI
  const [showToast,     setShowToast]     = useState(false);
  const [activeSection, setActiveSection] = useState('gateways');

  const handleSave = () => {
    const payload = {
      stripePublishable, stripeSecret, stripeWebhook, stripeEnabled,
      paystackPublic, paystackSecret, paystackWebhook, paystackEnabled,
      paypalClientId, paypalClientSecret, paypalWebhookId, paypalWebhook, paypalEnabled,
      razorpayKeyId, razorpayKeySecret, razorpayWebhook, razorpayWebhookSecret, razorpayEnabled,
      platformCommission, merchantCommission, donationFee,
      payoutSchedule, minPayoutAmount, autoPayouts, payoutNotifications,
      testMode, fraudProtection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.setItem('em_payment_keys', JSON.stringify({
      stripePublishable, paystackPublic, paypalClientId, razorpayKeyId,
    }));
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const sections = [
    { id: 'gateways',   label: 'API Keys',        icon: FiKey },
    { id: 'webhooks',   label: 'Webhooks',        icon: FiLink },
    { id: 'commission', label: 'Commission',      icon: FiPercent },
    { id: 'payouts',    label: 'Payout Schedule', icon: FiCalendar },
    { id: 'controls',   label: 'Controls',        icon: FiShield },
  ];

  const nextPayout = {
    daily:    'Tomorrow, 9:00 AM',
    weekly:   'Next Monday, 9:00 AM',
    biweekly: 'In 7 days, 9:00 AM',
    monthly:  '1st of Next Month, 9:00 AM',
  }[payoutSchedule];

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={FiDollarSign}  label="Total Processed"  value="$142.8K" sub="All-time"          color="bg-accent-navy" />
        <StatCard icon={FiTrendingUp}  label="This Month"       value="$18.4K"  sub="+12% vs last mo"   color="bg-green-500"   />
        <StatCard icon={FiPercent}     label="Platform Revenue" value="$14.3K"  sub="10% commission"    color="bg-accent-gold" />
        <StatCard icon={FiClock}       label="Pending Payouts"  value="$3.2K"   sub="6 merchants"       color="bg-amber-500"   />
      </div>

      {/* Section nav */}
      <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-1">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
          >
            <SafeIcon icon={s.icon} className="w-3.5 h-3.5" />
            {s.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeSection}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >

          {/* ── API KEYS ── */}
          {activeSection === 'gateways' && (
            <>
              {testMode && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3">
                  <SafeIcon icon={FiAlertCircle} className="text-amber-500 w-4 h-4 flex-shrink-0" />
                  <p className="text-[11px] text-amber-700 font-bold">
                    Test Mode Active — No real transactions will be processed. Switch to Live Mode in Controls when ready.
                  </p>
                </div>
              )}

              {/* Stripe */}
              <Section icon={FiCreditCard} title="Stripe" subtitle="Credit & Debit Card Processing — Global">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <KeyField label="Publishable Key (Client-side)" hint="Starts with pk_test_ or pk_live_" value={stripePublishable} onChange={setStripePublishable} />
                  <KeyField label="Secret Key (Server-side)"      hint="Starts with sk_test_ or sk_live_" value={stripeSecret}      onChange={setStripeSecret}      />
                </div>
                <InfoBox color="blue">
                  The Secret Key must be stored on your backend server. Never expose it in client-side code.
                  Stripe requires it to create Payment Intents and process charges.
                </InfoBox>
              </Section>

              {/* Paystack */}
              <Section icon={FiZap} title="Paystack" subtitle="Africa & Emerging Markets Gateway">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <KeyField label="Public Key (Client-side)"  hint="Starts with pk_test_ or pk_live_" value={paystackPublic}  onChange={setPaystackPublic}  />
                  <KeyField label="Secret Key (Server-side)"  hint="Starts with sk_test_ or sk_live_" value={paystackSecret}  onChange={setPaystackSecret}  />
                </div>
                <InfoBox color="green" emoji="🇳🇬">
                  Paystack supports NGN, GHS, ZAR, KES and USD. Ideal for African markets.
                  Use the Secret Key on your server to verify transactions via the Paystack Verify API.
                </InfoBox>
              </Section>

              {/* PayPal */}
              <Section icon={FiCreditCard} title="PayPal" subtitle="Digital Wallet & Buy Now Pay Later — Global">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <KeyField label="Client ID (Public)"          hint="Alphanumeric — safe for client-side"    value={paypalClientId}     onChange={setPaypalClientId}     />
                  <KeyField label="Client Secret (Server-side)" hint="Keep strictly server-side only"         value={paypalClientSecret} onChange={setPaypalClientSecret} />
                </div>
                <KeyField
                  label="Webhook ID"
                  hint="Found in PayPal Developer Dashboard → Webhooks → Webhook ID (not the URL)"
                  value={paypalWebhookId}
                  onChange={setPaypalWebhookId}
                />
                <InfoBox color="indigo" emoji="🅿">
                  PayPal supports 200+ markets and 25+ currencies. Use the REST SDK with your Client ID and
                  Client Secret to generate an OAuth 2.0 access token for server-side order creation and capture.
                  The Webhook ID is required to validate incoming PayPal event signatures.
                </InfoBox>
              </Section>

              {/* Razorpay */}
              <Section icon={FiZap} title="Razorpay" subtitle="India & South Asia Payment Gateway">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <KeyField label="Key ID (Public)"            hint="Starts with rzp_test_ or rzp_live_"     value={razorpayKeyId}     onChange={setRazorpayKeyId}     />
                  <KeyField label="Key Secret (Server-side)"   hint="Generated in Razorpay Dashboard → API Keys" value={razorpayKeySecret} onChange={setRazorpayKeySecret} />
                </div>
                <InfoBox color="sky" emoji="🔵">
                  Razorpay supports INR and 100+ payment methods including UPI, cards, netbanking, wallets,
                  and EMI. Ideal for Indian and South Asian markets. Use Key ID on the client and Key Secret
                  on the server to verify payment signatures.
                </InfoBox>
              </Section>
            </>
          )}

          {/* ── WEBHOOKS ── */}
          {activeSection === 'webhooks' && (
            <Section icon={FiLink} title="Webhook Endpoints" subtitle="Real-time event notifications from payment gateways">
              <div className="space-y-10">

                {/* Stripe */}
                <div className="space-y-4">
                  <GatewaySectionDivider gateway="stripe" />
                  <WebhookField
                    label="Stripe Webhook URL"
                    value={stripeWebhook}
                    onChange={setStripeWebhook}
                    hint="Configure in Stripe Dashboard → Developers → Webhooks."
                  />
                  <WebhookEvents events={['payment_intent.succeeded', 'charge.refunded', 'checkout.session.completed']} />
                </div>

                {/* Paystack */}
                <div className="space-y-4 border-t border-brand-dark pt-8">
                  <GatewaySectionDivider gateway="paystack" />
                  <WebhookField
                    label="Paystack Webhook URL"
                    value={paystackWebhook}
                    onChange={setPaystackWebhook}
                    hint="Configure in Paystack Dashboard → Settings → API Keys & Webhooks."
                  />
                  <WebhookEvents events={['charge.success', 'transfer.success', 'refund.processed']} />
                </div>

                {/* PayPal */}
                <div className="space-y-4 border-t border-brand-dark pt-8">
                  <GatewaySectionDivider gateway="paypal" />
                  <WebhookField
                    label="PayPal Webhook Listener URL"
                    value={paypalWebhook}
                    onChange={setPaypalWebhook}
                    hint="Register in PayPal Developer Dashboard → My Apps → Webhooks. Paste your server URL here."
                  />
                  <WebhookEvents events={['PAYMENT.CAPTURE.COMPLETED', 'PAYMENT.CAPTURE.REFUNDED', 'CHECKOUT.ORDER.APPROVED']} />
                  <InfoBox color="indigo" emoji="🅿">
                    PayPal event names follow SCREAMING_SNAKE_CASE. Use the Webhook ID saved under API Keys
                    together with your Client Secret to verify the <code className="bg-indigo-100 px-1 rounded">PayPal-Transmission-Sig</code> header on your server.
                  </InfoBox>
                </div>

                {/* Razorpay */}
                <div className="space-y-4 border-t border-brand-dark pt-8">
                  <GatewaySectionDivider gateway="razorpay" />
                  <WebhookField
                    label="Razorpay Webhook URL"
                    value={razorpayWebhook}
                    onChange={setRazorpayWebhook}
                    hint="Configure in Razorpay Dashboard → Settings → Webhooks → Add New Webhook."
                  />
                  <KeyField
                    label="Razorpay Webhook Secret"
                    hint="Set when creating the webhook in Razorpay Dashboard — used to verify HMAC-SHA256 signatures"
                    value={razorpayWebhookSecret}
                    onChange={setRazorpayWebhookSecret}
                  />
                  <WebhookEvents events={['payment.captured', 'payment.failed', 'refund.created']} />
                </div>

                {/* Global security note */}
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                  <SafeIcon icon={FiShield} className="text-amber-500 w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-amber-700 leading-relaxed">
                    Always validate webhook signatures on your server using each gateway's signing secret.
                    Never trust webhook payloads without signature verification — this prevents replay and spoofing attacks.
                  </p>
                </div>
              </div>
            </Section>
          )}

          {/* ── COMMISSION ── */}
          {activeSection === 'commission' && (
            <Section icon={FiPercent} title="Commission & Fee Structure" subtitle="Platform-wide revenue share configuration">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Platform Commission',   val: platformCommission, set: setPlatformCommission,
                    desc: 'Percentage taken from each merchant sale.',
                    preview: `On $100 sale → $${platformCommission} platform, $${100 - platformCommission} merchant`,
                    color: 'text-accent-navy' },
                  { label: 'Merchant Service Fee',  val: merchantCommission, set: setMerchantCommission,
                    desc: 'Additional fee charged for premium listing features.',
                    preview: `On $100 sale → $${merchantCommission} service fee`,
                    color: 'text-accent-gold' },
                  { label: 'Donation Processing Fee', val: donationFee, set: setDonationFee,
                    desc: 'Fee applied to memorial donation transactions.',
                    preview: `On $100 donation → $${donationFee} processing fee`,
                    color: 'text-green-600' },
                ].map(({ label, val, set, desc, preview, color }) => (
                  <div key={label} className="bg-brand-light rounded-[24px] border border-brand-dark p-6 space-y-4">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1">{label}</label>
                      <p className="text-[10px] text-text-muted leading-relaxed">{desc}</p>
                    </div>
                    <div className="relative">
                      <input
                        type="number" min="0" max="100" step="0.5"
                        value={val}
                        onChange={e => set(parseFloat(e.target.value) || 0)}
                        className="w-full pl-4 pr-10 py-3 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-bold text-accent-navy"
                      />
                      <span className={`absolute right-4 top-1/2 -translate-y-1/2 font-black text-sm ${color}`}>%</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="h-2 bg-white rounded-full overflow-hidden border border-brand-dark">
                        <motion.div animate={{ width: `${Math.min(val, 100)}%` }} className="h-full bg-accent-gold rounded-full" />
                      </div>
                      <p className="text-[9px] text-text-muted font-light">{preview}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-accent-navy rounded-[24px] p-6 grid grid-cols-3 gap-4">
                {[10000, 50000, 100000].map(gmv => (
                  <div key={gmv} className="text-center">
                    <p className="text-[9px] font-black uppercase tracking-widest text-white/40">At ${(gmv / 1000).toFixed(0)}K GMV</p>
                    <p className="text-2xl font-serif text-accent-gold mt-1">${(gmv * platformCommission / 100).toLocaleString()}</p>
                    <p className="text-[9px] text-white/30">platform revenue</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── PAYOUT SCHEDULE ── */}
          {activeSection === 'payouts' && (
            <Section icon={FiCalendar} title="Payout Schedule" subtitle="Control when merchants receive their earnings">
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {PAYOUT_SCHEDULES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setPayoutSchedule(s.id)}
                      className={`p-5 rounded-[24px] border-2 text-left transition-all ${payoutSchedule === s.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark bg-brand-light hover:border-accent-gold/40'}`}
                    >
                      <div className="text-2xl mb-3">{s.icon}</div>
                      <p className="font-black text-accent-navy text-sm">{s.label}</p>
                      <p className="text-[10px] text-text-muted mt-1">{s.desc}</p>
                      {payoutSchedule === s.id && (
                        <div className="mt-3 flex items-center gap-1 text-accent-gold text-[9px] font-black uppercase tracking-widest">
                          <SafeIcon icon={FiCheck} className="w-3 h-3" /> Active
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Minimum Payout Amount (USD)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-accent-navy">$</span>
                      <input
                        type="number" min="0" step="5"
                        value={minPayoutAmount}
                        onChange={e => setMinPayoutAmount(parseFloat(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-bold"
                      />
                    </div>
                    <p className="text-[10px] text-text-muted">Merchants must reach this balance before a payout is triggered.</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Next Scheduled Payout</label>
                    <div className="p-4 bg-brand-light border-2 border-brand-dark rounded-2xl flex items-center gap-3">
                      <SafeIcon icon={FiCalendar} className="text-accent-gold w-5 h-5" />
                      <div>
                        <p className="font-bold text-accent-navy text-sm">{nextPayout}</p>
                        <p className="text-[10px] text-text-muted">UTC timezone</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <ToggleRow label="Automatic Payouts"     desc="Automatically process payouts on schedule without manual approval."               value={autoPayouts}         onChange={setAutoPayouts}         />
                  <ToggleRow label="Payout Notifications"  desc="Send email alerts to merchants when their payout has been processed."              value={payoutNotifications} onChange={setPayoutNotifications} />
                </div>
              </div>
            </Section>
          )}

          {/* ── CONTROLS ── */}
          {activeSection === 'controls' && (
            <Section icon={FiShield} title="Gateway Controls & Security" subtitle="Enable, disable, and secure payment processing">
              <div className="space-y-3">
                <ToggleRow label="Stripe Enabled"    desc="Allow customers to pay using Stripe (Credit / Debit cards, global)."     value={stripeEnabled}    onChange={setStripeEnabled}    />
                <ToggleRow label="Paystack Enabled"  desc="Allow customers to pay using Paystack (Africa & emerging markets)."       value={paystackEnabled}  onChange={setPaystackEnabled}  />
                <ToggleRow label="PayPal Enabled"    desc="Allow customers to pay using PayPal (digital wallet, BNPL, 200+ markets)." value={paypalEnabled}    onChange={setPaypalEnabled}    />
                <ToggleRow label="Razorpay Enabled"  desc="Allow customers to pay using Razorpay (UPI, cards, wallets — India & South Asia)." value={razorpayEnabled}  onChange={setRazorpayEnabled}  />
                <ToggleRow label="Test / Sandbox Mode" desc="Use sandbox credentials. No real money is processed. Disable for production." value={testMode}         onChange={setTestMode}         />
                <ToggleRow label="Fraud Protection"  desc="Enable Stripe Radar, Paystack fraud detection, and Razorpay Shield on all transactions." value={fraudProtection}  onChange={setFraudProtection}  />
              </div>

              {/* Status grid — all 4 gateways */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                <GatewayStatusPill gateway="stripe"   enabled={stripeEnabled}   testMode={testMode} />
                <GatewayStatusPill gateway="paystack" enabled={paystackEnabled} testMode={testMode} />
                <GatewayStatusPill gateway="paypal"   enabled={paypalEnabled}   testMode={testMode} />
                <GatewayStatusPill gateway="razorpay" enabled={razorpayEnabled} testMode={testMode} />
              </div>
            </Section>
          )}

        </motion.div>
      </AnimatePresence>

      {/* Save */}
      <div className="flex justify-end pt-4">
        <button
          onClick={handleSave}
          className="flex items-center gap-3 px-10 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Payment Settings
        </button>
      </div>

      <SaveToast show={showToast} />
    </div>
  );
};

export default PaymentSettings;